/**
 * Favorites store — TanStack Query bindings for /library/favorites.
 *
 * Replaces the previous local-state-with-mock-seed implementation
 * (UI bug 2026-04-29) which pre-populated every fresh tenant with
 * 7 zombie items pointing at deleted documents. That violated the
 * "no mock data in production paths" rule.
 *
 * Public surface:
 *   - useFavorites()            returns { items, isLoading, isError, error, refetch }
 *   - useStarState(item)        'starred' | 'unstarred' | 'unknown' for THIS item
 *                               (id + kind); 'unknown' until the read has answered
 *                               (and after it failed) — never a guessed 'unstarred'
 *   - useStarWriteInFlight(item) a star/unstar of THIS item is waiting on the server
 *   - star(item) / unstar(item) THE one path that writes /library/favorites
 *
 * ONE PERSONAL STAR (decision 38, Library LB6-T5, L6-09). A star names its
 * item's id AND kind ({ id, kind } — a document, an article included, or a
 * folder), taken from the row it sits on, never guessed. `star` / `unstar`
 * await the server: success is announced only after a 2xx, a failure is an
 * error toast, and either way the favourites are re-read so every star shows
 * the server's state. The legacy `toggleStar(itemId, meta?)` shim (it guessed
 * 'document' when no kind was given, swallowed every error, and let the
 * caller toast success before the request) is deleted, as are the unused
 * mutation hook (decision 43) and the older `unstar(starId)` /
 * `isStarred(itemId)` (LB3T5-R-03 / BF-04).
 */

import { useMemo, useSyncExternalStore } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { queryClient } from '@/lib/query-client';
import { DocumentKindSchema, type DocumentKind } from '@argusai/shared';
import type { StarredItem } from '@/types/library';

// ── Types matching the API response ──────────────────────────────────────

/**
 * Wire-format favorite returned from GET /library/favorites. Matches
 * FavoriteRowSchema in the API route exactly.
 */
export interface ApiFavorite {
  id: string;
  itemId: string;
  itemType: 'uploaded' | 'wiki' | 'external' | 'folder';
  /** The document's stored kind (decision 19); null for a folder. */
  documentKind: DocumentKind | null;
  title: string;
  source: StarredItem['source'];
  status: string;
  starredAt: string;
  author: string | null;
  updatedAt: string | null;
  mimeType: string | null;
  fileName: string | null;
}

/**
 * Discriminator used by the API (FAVORITE_ITEM_KINDS, user-favorites.ts):
 * 'document' | 'folder' | 'external_source'. Since decision 19 a wiki
 * article IS a document, so it is starred, listed and unstarred as a
 * 'document' — one item kind for one noun (c41 migrated the older article
 * favourites; the API answers 400 for any other kind).
 */
export type FavoriteItemKind =
  | 'document'
  | 'folder'
  | 'external_source';

/**
 * Maps the legacy `itemType` vocabulary to the API `itemKind`. The API
 * still LISTS an article favourite as itemType 'wiki' (its source and
 * article state); its kind is 'document' (LB3T4-BF-04).
 */
export function itemTypeToKind(
  itemType: 'uploaded' | 'wiki' | 'external' | 'folder',
): FavoriteItemKind {
  switch (itemType) {
    case 'uploaded':
      return 'document';
    case 'wiki':
      return 'document';
    case 'folder':
      return 'folder';
    case 'external':
      return 'external_source';
  }
}

/**
 * What a Favorites row IS, for its icon, its type badge and the Type filter:
 * 'folder' for a folder favourite, otherwise the document's STORED kind as
 * the API serves it (`documentKind` — the same kind the Documents list
 * shows; LB3T5-R-01 / BF-01). It is never worked out from the itemType: the
 * API lists a synced document as 'uploaded', so inverting the itemType made
 * every synced favourite a File and the Synced filter always empty.
 *
 * null when the row carries no kind this build knows (an external_source
 * star, which no document row is, or a response from an API that predates
 * the field): such a row is shown as no kind, matches no Type filter but
 * 'all', and does not open (favoriteOpenTarget).
 */
export function favoriteDisplayKind(
  item: Pick<StarredItem, 'itemType' | 'documentKind'>,
): DocumentKind | 'folder' | null {
  if (item.itemType === 'folder') return 'folder';
  const parsed = DocumentKindSchema.safeParse(item.documentKind);
  return parsed.success ? parsed.data : null;
}

/**
 * Where opening a (non-folder) Favorites row goes: an article opens the
 * article reader, any other document its document page. A folder row
 * expands in place instead (null).
 */
export function favoriteOpenTarget(
  item: Pick<StarredItem, 'itemType' | 'itemId' | 'documentKind'>,
):
  | { to: '/library/article/$articleId'; params: { articleId: string } }
  | { to: '/library/doc/$docId'; params: { docId: string } }
  | null {
  const kind = favoriteDisplayKind(item);
  // A folder expands in place; a row of no known kind goes nowhere (an
  // external_source path is not a document id).
  if (kind === 'folder' || kind === null) return null;
  if (kind === 'article') return { to: '/library/article/$articleId', params: { articleId: item.itemId } };
  return { to: '/library/doc/$docId', params: { docId: item.itemId } };
}

// ── Cache key ─────────────────────────────────────────────────────────────

/**
 * Single root key for everything favorites-related. Mutations
 * invalidate this exact key so the whole list refetches in one shot.
 */
export const FAVORITES_QUERY_KEY = ['library', 'favorites'] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────

interface ListResponse {
  data: ApiFavorite[];
  total: number;
}

/**
 * The one read of the favourites: the key, the request and the staleTime every
 * subscriber shares (the Favorites page's rows and every star).
 */
const favoritesQueryOptions = {
  queryKey: FAVORITES_QUERY_KEY,
  queryFn: () => apiClient.get<ListResponse>('/library/favorites'),
  // Favorites change rarely; 30s staleTime is plenty and avoids
  // refetching every time the user clicks between Library tabs.
  staleTime: 30 * 1000,
};

/**
 * Subscribe to the current user's favorites: the query plus its rows as
 * `items`. Whether one item is starred is `useStarState(item)` — by id AND
 * kind; the id-only `starredIds` set (which could light a document's star
 * for a folder favourite of the same id) went with LB6-T5. `items` is empty
 * while the read is pending or failed: a caller that draws it checks
 * `isLoading` / `isError` first (the Favorites page does).
 *
 * `items` are mapped to StarredItem to keep the existing render code
 * untouched. The frontend StarredItem shape is a strict superset of
 * what the API returns (`syncError`, `userId`) — we leave the optional
 * fields undefined and the API never emits a syncError today, so any
 * UI that conditionally renders on `item.syncError` simply never
 * triggers (matches reality: the connector failure UX still has to be
 * wired through this surface as a follow-up).
 */
export function useFavoritesQuery(): UseQueryResult<ListResponse> & {
  items: StarredItem[];
} {
  const query = useQuery<ListResponse>(favoritesQueryOptions);

  // Mapped once per server answer, not on every render (LB6-T5-R-02).
  const data = query.data;
  const items = useMemo<StarredItem[]>(
    () =>
      (data?.data ?? []).map((api) => ({
        id: api.id,
        userId: 'current-user', // legacy field — no longer load-bearing
        itemId: api.itemId,
        itemType: api.itemType,
        documentKind: api.documentKind,
        title: api.title,
        source: api.source,
        status: api.status,
        starredAt: api.starredAt,
        author: api.author ?? undefined,
        updatedAt: api.updatedAt ?? undefined,
        mimeType: api.mimeType ?? undefined,
        fileName: api.fileName ?? undefined,
      })),
    [data],
  );
  return Object.assign(query, { items });
}

/**
 * Legacy name kept to minimize the diff in consumers — re-exports
 * useFavoritesQuery under the original `useFavorites` identifier.
 */
export const useFavorites = useFavoritesQuery;

// ── The one star: what is starred, and the one path that changes it ───────

/**
 * What a star names: the item's id AND its kind, both from the row it sits on
 * (a document row — a file, a synced document or an article, decision 19 — is
 * kind 'document'; a folder row is kind 'folder'). The kind is never guessed:
 * the API keys a favourite by (itemId, itemKind), so a star sent with the
 * wrong kind stars something else or unstars nothing.
 */
export interface StarTarget {
  id: string;
  kind: FavoriteItemKind;
}

/** The star target of a Favorites row: its id and the kind the API listed it under. */
export function favoriteStarTarget(item: Pick<StarredItem, 'itemId' | 'itemType'>): StarTarget {
  return { id: item.itemId, kind: itemTypeToKind(item.itemType) };
}

/** The key a star is known by: its kind AND its id (the API keys a favourite by both). */
function starKey(item: StarTarget): string {
  return `${item.kind}:${item.id}`;
}

const starKeysByAnswer = new WeakMap<ListResponse, ReadonlySet<string>>();

/**
 * The (kind, id) keys of every favourite in one server answer, built ONCE per
 * answer and shared by every star that reads it (LB6-T5-R-02): a table of N
 * stars over M favourites costs one set of M keys, not N re-mappings of the
 * list on every render. It is the `select` of each star's subscription.
 */
export function favoriteStarKeys(answer: ListResponse): ReadonlySet<string> {
  let keys = starKeysByAnswer.get(answer);
  if (!keys) {
    keys = new Set(answer.data.map((f) => starKey({ id: f.itemId, kind: itemTypeToKind(f.itemType) })));
    starKeysByAnswer.set(answer, keys);
  }
  return keys;
}

/**
 * What a star can say about its item. 'pending' (the read has not answered
 * yet) and 'failed' (the read failed — including a re-read that failed after
 * an earlier answer) are NOT 'unstarred': a read that did not answer is never
 * shown as an empty list (principle 4), and nothing acts on it.
 */
export type StarState = 'starred' | 'unstarred' | 'pending' | 'failed';

/**
 * Is this item (id AND kind) starred, as the server last answered — or is that
 * not known. One O(1) lookup in the memoised key set of the live favourites
 * query, so every star on the page shows the server's answer.
 */
export function useStarState(item: StarTarget): StarState {
  const { data: keys, status } = useQuery({ ...favoritesQueryOptions, select: favoriteStarKeys });
  if (status === 'error') return 'failed';
  if (status !== 'success' || keys === undefined) return 'pending';
  return keys.has(starKey(item)) ? 'starred' : 'unstarred';
}

// ── One write per item at a time, whichever control asked (LB6-T5-BF-02) ──

const writesInFlight = new Set<string>();
const writeListeners = new Set<() => void>();

function notifyWriteListeners(): void {
  for (const listener of writeListeners) listener();
}

function subscribeToWrites(listener: () => void): () => void {
  writeListeners.add(listener);
  return () => {
    writeListeners.delete(listener);
  };
}

/**
 * True while a star or unstar of this item is waiting on the server — from ANY
 * control (the item's StarButton, another StarButton for the same item, the
 * Favorites page's "Unstar" menu item). Controls draw it as busy.
 */
export function useStarWriteInFlight(item: StarTarget): boolean {
  const key = starKey(item);
  const read = () => writesInFlight.has(key);
  return useSyncExternalStore(subscribeToWrites, read, read);
}

/**
 * Run one write for this item unless one is already waiting on the server; a
 * second ask while it is in flight is ignored (resolves false, sends nothing),
 * so two controls can never race two requests for the same favourite.
 */
async function oneWriteAtATime(item: StarTarget, write: () => Promise<boolean>): Promise<boolean> {
  const key = starKey(item);
  if (writesInFlight.has(key)) return false;
  writesInFlight.add(key);
  notifyWriteListeners();
  try {
    return await write();
  } finally {
    writesInFlight.delete(key);
    notifyWriteListeners();
  }
}

/**
 * Re-read the favourites so every star and the Favorites page show the
 * server's state. Awaited: by the time star/unstar resolve, the cache holds
 * the answer (a failed re-read leaves the query in its error state: the
 * Favorites page shows the failure and every star reads 'failed' — never a
 * guessed list).
 */
async function rereadFavorites(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY });
}

/**
 * Ask the server for the favourites now, whether or not anything on the page
 * is subscribed: the answer (also written to the cache), or null when the
 * read failed (the query is then in its error state).
 */
async function readFavoritesNow(): Promise<ListResponse | null> {
  try {
    return await queryClient.fetchQuery({ ...favoritesQueryOptions, staleTime: 0 });
  } catch {
    return null;
  }
}

function isNotFound(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { status?: unknown }).status === 404;
}

/**
 * Star one item for the signed-in person — THE one path that writes
 * POST /library/favorites (L6-09). It awaits the server: the success toast is
 * shown only after a 2xx; a refused or failed request shows an error toast
 * and the star stays as the server has it (nothing was shown as starred
 * before the answer, so there is nothing to undo but the re-read). Resolves
 * true when the server took it. Never rejects: the failure is reported here,
 * once. A star of an item already starred is the state asked for (the POST is
 * idempotent: 200, created false). While a write for this item is in flight a
 * second ask, from any control, is ignored and resolves false.
 */
export async function star(item: StarTarget): Promise<boolean> {
  return oneWriteAtATime(item, async () => {
    try {
      await apiClient.post('/library/favorites', { itemId: item.id, itemKind: item.kind });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not add to favorites'));
      await rereadFavorites();
      return false;
    }
    toast.success('Added to favorites');
    await rereadFavorites();
    return true;
  });
}

/**
 * Unstar one item — the one path that writes DELETE /library/favorites/:kind/:id,
 * with the same contract as `star`: the success toast after a 2xx, an error
 * toast and the server's state on failure, one write per item at a time.
 *
 * Like a star of a starred item, an unstar of a star already gone (removed in
 * another tab — the cache can be 30 s old) is the state asked for
 * (LB6-T5-BF-03). The DELETE answers 404 then, so on a 404 the favourites are
 * read again and, only if that read SUCCEEDS and no longer lists the item, it
 * is reported as removed. A 404 while the server still lists it (a wrong
 * route or kind), or a failed read, stays an error.
 */
export async function unstar(item: StarTarget): Promise<boolean> {
  return oneWriteAtATime(item, async () => {
    try {
      await apiClient.delete(
        `/library/favorites/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.id)}`,
      );
    } catch (err) {
      if (isNotFound(err)) {
        const fresh = await readFavoritesNow();
        if (fresh && !favoriteStarKeys(fresh).has(starKey(item))) {
          toast.success('Removed from favorites');
          return true;
        }
        // The read above already put the server's state (or its failure) in the cache.
        toast.error(getApiErrorMessage(err, 'Could not remove from favorites'));
        return false;
      }
      toast.error(getApiErrorMessage(err, 'Could not remove from favorites'));
      await rereadFavorites();
      return false;
    }
    toast.success('Removed from favorites');
    await rereadFavorites();
    return true;
  });
}

